/**
 * Operation Management - Stats Cards
 *
 * 6 cards in 2 rows matching Figma design 573:28047
 * Each card: icon + two-part label at top-end, large number centered below
 */

import { Box, Typography } from '@mui/material';
import { LuCalendar, LuBuilding, LuClock, LuZap, LuCheckCircle, LuXCircle } from 'react-icons/lu';
import type { IMaintenanceTaskStats } from '../types';

interface IOperationStatsCardsProps {
  stats: IMaintenanceTaskStats;
}

const CARD_STYLES = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '76px',
  padding: '24px',
  borderRadius: '16px',
  backgroundColor: '#FFFFFF',
  minHeight: '100px',
  flex: '1 1 0',
};

const OperationStatsCards = ({ stats }: IOperationStatsCardsProps) => {
  const cards = [
    {
      icon: <LuCalendar size={24} color="#5B3FFF" />,
      label: stats.scheduled.label,
      value: stats.scheduled.count,
    },
    {
      icon: <LuBuilding size={24} color="#5B3FFF" />,
      label: stats.assigned.label,
      value: stats.assigned.count,
    },
    {
      icon: <LuClock size={24} color="#5B3FFF" />,
      label: stats.pending.label,
      value: stats.pending.count,
    },
    {
      icon: <LuZap size={24} color="#5B3FFF" />,
      label: stats.inProgress.label,
      value: stats.inProgress.count,
    },
    {
      icon: <LuCheckCircle size={24} color="#5B3FFF" />,
      label: stats.completed.label,
      value: stats.completed.count,
    },
    {
      icon: <LuXCircle size={24} color="#5B3FFF" />,
      label: stats.cancelled.label,
      value: stats.cancelled.count,
    },
  ];

  const renderCard = (card: (typeof cards)[0], i: number) => {
    // API labels may contain " / " to separate main label and sub-label
    const parts = card.label.split(' / ');
    const mainLabel = parts[0] || '';
    const subLabel = parts[1] || '';

    return (
    <Box key={i} sx={CARD_STYLES}>
      {/* Text content — right side in visual RTL, stacked: label row + number */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '18px', alignItems: 'flex-start', justifyContent: 'start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          {subLabel && (
            <>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', fontWeight: 400, color: '#435363', whiteSpace: 'nowrap', textAlign: 'right' }}>
                {subLabel}
              </Typography>
              <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#435363', flexShrink: 0 }} />
            </>
          )}
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', fontWeight: 400, color: '#435363', whiteSpace: 'nowrap', textAlign: 'right' }}>
            {mainLabel}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 700,
            fontSize: '32px',
            color: '#5B3FFF',
            textAlign: 'right',
            lineHeight: '100%',
          }}
        >
          {card.value}
        </Typography>
      </Box>
      {/* Icon — bottom-left in visual (in RTL this is end side) */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {card.icon}
      </Box>
    </Box>
  );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Row 1: first 3 cards */}
      <Box sx={{ display: 'flex', gap: '12px' }}>
        {cards.slice(0, 3).map(renderCard)}
      </Box>
      {/* Row 2: last 3 cards */}
      <Box sx={{ display: 'flex', gap: '12px' }}>
        {cards.slice(3, 6).map((card, i) => renderCard(card, i + 3))}
      </Box>
    </Box>
  );
};

export default OperationStatsCards;
